import { UserAddOutlined } from '@ant-design/icons'
import { RoleGroup } from '@community_dev/types/lib/api/v1/Roles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, ConfigProvider, notification, Table } from 'antd'
import isEqual from 'lodash/isEqual'
import { useMemo, useState, type JSX } from 'react'
import styled from 'styled-components'

import { TABLE_PAGINATION_THEME } from '../../theme'
import { SettingsCard, SettingsPage, SettingsRows } from '../Settings'

import { DataExportToggle } from './DataExportToggle'
import { InviteForm } from './InviteForm'
import { Mfa } from './Mfa'
import { PendingInviteEditModal } from './PendingInviteEditModal'
import { useTeamMembers, useDataDownloadAccess } from './queries'
import { TeamMemberEditModal, TeamMemberValue } from './TeamMemberEditModal'
import { useTeamColumnDef } from './useTeamsColumnDef'

import {
  Client,
  ClientSeat,
  deleteRemoveInvite,
  deleteTeamMember,
  getRoles,
  PendingInvite,
  postSendInvite,
  putEditTeamMember,
  putUpdateInvite,
  Role,
  transferProtectedRole,
  updateSeatRoles,
} from 'api/clients'
import { QUERIES } from 'constants/queries'
import { SeatRoles } from 'constants/roles'
import { toInviteRoles } from 'utils/roles'

const TEAM_MEMBERS_PAGE_SIZE = 25

const StyledTable = styled.div`
  .ant-table-wrapper .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: 16px 24px;
  }
`

function showError() {
  notification.error({
    message: 'Sorry, something went wrong. Please try again.',
    placement: 'bottomRight',
    duration: 2,
  })
}

function showSuccess(message: string) {
  notification.success({
    message,
    placement: 'bottomRight',
    duration: 2,
  })
}

export function Team({ client }: { client: Client }): JSX.Element {
  const queryClient = useQueryClient()
  const [editMember, setEditMember] = useState<ClientSeat>()
  const [editPendingInvite, setEditPendingInvite] = useState<PendingInvite>()
  const [showAdd, setShowAdd] = useState(false)
  const teamMembersObj = useTeamMembers(client.id)

  const { teamMembers, members, pendingMembers, isLoading, isMembersError, isPendingMembersError } = teamMembersObj

  const { data: roles = [] } = useQuery([QUERIES.ROLES], getRoles, { staleTime: Infinity })

  // an owner exists or has been invited.
  const ownerRoleExists = useMemo(() => {
    return (
      members.some((member) => member.roles.some((role) => role.name === SeatRoles.OWNER)) ||
      pendingMembers.some((member) => member.role.name === SeatRoles.OWNER)
    )
  }, [members, pendingMembers])

  const invitableRoles = useMemo(() => {
    if (ownerRoleExists) {
      // if there is an owner, we cannot invite a new owner
      return roles.filter((role) => role.name !== SeatRoles.OWNER)
    } else {
      // if there isn’t an owner, we can only invite owners
      return roles.filter((role) => role.name === SeatRoles.OWNER)
    }
  }, [ownerRoleExists, roles])

  // The primary role that can be assigned when inviting/editing a pending invite.
  const invitablePrimaryRoles = invitableRoles.filter((role) => role.group === RoleGroup.PRIMARY)

  const { isDownloadEnabled, isDownloadEnabledLoading, enableClientDownload, disableClientDownload } =
    useDataDownloadAccess({ clientId: client.id })

  const { mutateAsync: sendInvite } = useMutation(postSendInvite, {
    onError: showError,
    onSuccess() {
      showSuccess('Invite sent.')
      queryClient.invalidateQueries([QUERIES.PENDING_INVITES, { id: client.id }])
      setShowAdd(false)
    },
  })

  const { mutate: deleteInvite } = useMutation(deleteRemoveInvite, {
    onError: showError,
    onSuccess() {
      showSuccess('Invite deleted.')
      queryClient.invalidateQueries([QUERIES.PENDING_INVITES, { id: client.id }])
    },
  })
  const { mutateAsync: updateInvite } = useMutation(putUpdateInvite, {
    onError: showError,
    onSuccess() {
      showSuccess('Invite updated.')
      queryClient.invalidateQueries([QUERIES.PENDING_INVITES, { id: client.id }])
      setEditPendingInvite(undefined)
    },
  })
  const { mutate: saveTeamMember } = useMutation(putEditTeamMember, {
    onError: showError,
    onSuccess() {
      showSuccess('Team member saved.')
      queryClient.invalidateQueries([QUERIES.TEAM_MEMBERS, { id: client.id }])
      setEditMember(undefined)
    },
  })
  const { mutate: saveTeamMemberRoles } = useMutation(
    async ({
      seatMappingId,
      roles,
      isOwnerTransfer,
    }: {
      seatMappingId: string
      roles: Role[]
      isOwnerTransfer: boolean
    }) => {
      const ownerRole = roles.find((role) => role.name === SeatRoles.OWNER)
      const nonOwnerRoles = roles.filter((role) => role.name !== SeatRoles.OWNER)
      // owner roles must be transferred from one seat to another:
      if (isOwnerTransfer && ownerRole) {
        await transferProtectedRole({ seatMappingId, role: ownerRole, clientId: client.id })
      }
      if (nonOwnerRoles.length > 0) {
        // regular roles are updated
        await updateSeatRoles({ seatMappingId, roles: roles })
      }
    },
    {
      onError: showError,
      onSuccess() {
        queryClient.invalidateQueries([QUERIES.TEAM_MEMBERS, { id: client.id }])
      },
    },
  )
  const { mutate: deleteMember } = useMutation(deleteTeamMember, {
    onError: showError,
    onSuccess() {
      showSuccess('Team member deleted.')
      queryClient.invalidateQueries([QUERIES.TEAM_MEMBERS, { id: client.id }])
    },
  })

  function handleRemove(record: ClientSeat) {
    return deleteMember({ memberId: record.id, id: client.id })
  }

  async function handleSendInvite({
    email,
    role,
    roles: submittedRoles,
  }: {
    email: string
    role?: { name: string }
    roles?: Role[]
  }) {
    const resolvedRoles = toInviteRoles({ role, roles: submittedRoles }, roles)
    const body =
      resolvedRoles.length > 0 ? { email, role_ids: resolvedRoles.map((r) => r.id) } : { email, role: role?.name }
    try {
      await sendInvite({ id: client.id, body })
    } catch {
      // errors are surfaced via the mutation's onError handler
    }
  }

  async function handleUpdatePendingInvite(inviteId: string, rolesToSet: Role[]) {
    try {
      await updateInvite({ id: client.id, inviteId, roleIds: rolesToSet.map((r) => r.id) })
    } catch {
      // errors are surfaced via the mutation's onError handler
    }
  }

  function handleEditSave(value: TeamMemberValue) {
    if (editMember !== undefined) {
      if (!isEqual(value.roles, editMember.roles)) {
        saveTeamMemberRoles({
          seatMappingId: editMember.seatMappingId,
          roles: value.roles,
          isOwnerTransfer:
            editMember.roles.every((role) => role.name !== SeatRoles.OWNER) &&
            value.roles.some((role) => role.name === SeatRoles.OWNER),
        })
      }
      return saveTeamMember({ id: client.id, memberId: editMember.id, body: value })
    }
  }

  const columnDefs = useTeamColumnDef(
    client.id,
    teamMembers,
    isLoading,
    setEditMember,
    handleSendInvite,
    handleRemove,
    deleteInvite,
    setEditPendingInvite,
  )
  return (
    <SettingsPage description="Team members, invitations and account security." title="Team">
      {(isMembersError || isPendingMembersError) && (
        <Alert message="Sorry could not load team members. Please try again." showIcon type="error" />
      )}
      <SettingsCard title="Security">
        <SettingsRows>
          <Mfa clientId={client.id} />
          {!isDownloadEnabledLoading && (
            <DataExportToggle
              client={client}
              disable={disableClientDownload}
              enable={enableClientDownload}
              isDataDownloadEnabled={isDownloadEnabled}
            />
          )}
        </SettingsRows>
      </SettingsCard>
      <SettingsCard
        extra={
          <Button icon={<UserAddOutlined />} onClick={() => setShowAdd((previous) => !previous)} type="primary">
            {ownerRoleExists ? 'Invite team member' : 'Invite account owner'}
          </Button>
        }
        title="Team Members"
      >
        <ConfigProvider theme={TABLE_PAGINATION_THEME}>
          <StyledTable>
            <Table
              columns={columnDefs}
              dataSource={teamMembers}
              loading={isLoading}
              pagination={{ defaultPageSize: TEAM_MEMBERS_PAGE_SIZE, hideOnSinglePage: true }}
              rowKey="id"
              scroll={{ x: 'max-content' }}
            />
          </StyledTable>
        </ConfigProvider>
      </SettingsCard>
      <InviteForm
        allRoles={roles}
        buttonName="Invite"
        client={client}
        isDownloadEnabled={isDownloadEnabled || false}
        isVisible={showAdd}
        onSubmit={handleSendInvite}
        primaryRoles={invitablePrimaryRoles}
        setIsVisible={setShowAdd}
        title="Invite Team Member"
      />
      {editMember && (
        <TeamMemberEditModal
          allRoles={roles}
          client={client}
          isDownloadEnabled={isDownloadEnabled || false}
          onCancel={() => setEditMember(undefined)}
          onSubmit={handleEditSave}
          value={editMember}
        />
      )}
      {editPendingInvite && (
        <PendingInviteEditModal
          allRoles={roles}
          client={client}
          invite={editPendingInvite}
          isDownloadEnabled={isDownloadEnabled || false}
          onCancel={() => setEditPendingInvite(undefined)}
          onSubmit={(selectedRoles) => handleUpdatePendingInvite(editPendingInvite.id, selectedRoles)}
          primaryRoles={invitablePrimaryRoles}
        />
      )}
    </SettingsPage>
  )
}
